import React, { useEffect, useState, Fragment } from 'react';
import { Auth } from 'aws-amplify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  Modal,
  Popconfirm,
  Image,
  Card,
  Button,
  Tag,
  Spin,
  Empty
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import AnimateHeight from 'react-animate-height';
import moment from 'moment';
import { EditOutlined, ProfileOutlined, DeleteOutlined } from '@ant-design/icons';
import CheckoutFlow from './checkout/CheckoutFlow';
import config from '../config';
import AddAStudentForm from './AddAStudentForm';
import ClassForm from './ClassForm';
import CenTable from './CenTable';
import ClassDescriptionContainer from './ClassDescriptionContainer';
import errorImg from '../imgs/error.png';
import { user } from '../lib/authLib';
import { useAppContext } from '../lib/contextLib';
import './ClassCard.scss';

const promise = loadStripe(config.STRIPE_KEY);
export default function ClassCard({ card, getClasses, setClassesUI, allDisplayedCardsData, allCardsData }) {
  const { isAuthenticated } = useAppContext();
  const {
    uuid,
    price,
    startDate,
    endDate,
    daysOfWeek,
    startTime,
    endTime,
    instructors,
    img,
    leadFollowClass,
    title,
    content,
    attendees,
    classClosedDate = false,
  } = card;

  const [reChecking, setReChecking] = useState(false);
  const [shownModal, setShownModal] = useState(null);
  const [classAttendees, setClassAttendees] = useState(attendees);
  const [attendeePositions, setAttendeePositions] = useState({});
  const [displayAddStudentForm, setDisplayAddStudentForm] = useState(0);
  const dropdownAnimationTime = 500; // ms

  useEffect(() => {
    const totalPositions = classAttendees.reduce((accumulator, attendee) => {
      const { position } = attendee;

      accumulator[position] = accumulator[position] ? ++accumulator[position] : 1;

      return accumulator;
    }, {});

    setAttendeePositions(totalPositions);
  }, [classAttendees]);

  function generateColumnSchema(humanFriendlyTitle, dataKey, editable) {
    return {
      title: humanFriendlyTitle,
      key: dataKey,
      sort: true,
      filter: true,
      print: true,
      editable,
      sum: false,
    };
  }

  function tagWithTextAndColor(text, tagColor) {
    return (
      <Tag color={tagColor}>
        {text.toUpperCase()}
      </Tag>
    );
  }

  async function onSave(data, newData) {
    const currentUserSession = await Auth.currentSession();
    try {
      await window.fetch(`${config.serverUrl}classes/create/${user[0].email}`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'accesstoken': currentUserSession.getAccessToken().getJwtToken(),
        },
        body: JSON.stringify({ ...card, attendees: newData }),
      });
      setClassAttendees(newData);
    } catch (error) {
      console.error(error);
    }
  }

  function genTagForPosition(position) {
    let color;

    if (/lead/ig.test(position)) {
      color = 'purple';
    } else if (/follow/ig.test(position)) {
      color = 'orange';
    } else { // either
      color = 'blue';
    }

    return tagWithTextAndColor(position, color);
  }

  const is_admin = user[0].role === "admin";
  const columnsSchema = [
    generateColumnSchema('Name', 'name', false),
    generateColumnSchema('E-mail', 'email', false),
    generateColumnSchema('Date Added', 'dateAdded', is_admin),
    Object.assign(generateColumnSchema('Position', 'position', true), { render: genTagForPosition }),
    Object.assign(generateColumnSchema('Status', 'paymentStatus', true), {
      render: (paymentStatus) => {
        let color;

        if (/Unpaid/ig.test(paymentStatus)) {
          color = 'cyan';
        } else if (/Paid/ig.test(paymentStatus)) {
          color = 'green';
        } else if (/Waitlisted/ig.test(paymentStatus)) {
          color = 'yellow';
        } else { // Removed
          color = 'gray';
        }

        return tagWithTextAndColor(paymentStatus, color);
      },
    }),
    generateColumnSchema('Paid', 'amountPaid', is_admin),
    generateColumnSchema('Notes', 'notes', true),
  ];
  const showButtons = isAuthenticated && user[0].role !== 'student';

  /**
   * Calculates whether or not students can register for a class.
   *
   * @param {string} start - Class start date.
   * @param {string|boolean} closedDate - Day to close class registration.
   *
   * @return {boolean} Can the students register?
   *
   * @example
   *
   *     if (studentsCanRegister(startDate, classClosedDate)) {
   */
  function studentsCanRegister(start, closedDate = false) {
    const today = new Date();
    const daysFromPast = (cutoffOrStart) => moment(today).diff(cutoffOrStart, 'days');

    if (closedDate) {
      return !(daysFromPast(closedDate) > 0);
    } else {
      const weeks = 3;
      const daysPerWeek = 7;
      const numberOfDaysAStudentCanRegisterAfterClassStartDate = weeks * daysPerWeek;
      const numberOfDaysTodayIsInTheFutureFromClassStartDate = daysFromPast(start);

      return !(
        numberOfDaysTodayIsInTheFutureFromClassStartDate > numberOfDaysAStudentCanRegisterAfterClassStartDate
      );
    }
  }

  const studentRegistrationEnabled = studentsCanRegister(startDate, classClosedDate);

  function formatTime(time) {
    return moment(time, 'hh mm a').format('hh:mm a').replace(/^0(?:0:0?)?/, '');
  }

  function positionCount(position) {
    return attendeePositions[position] && (
      <span style={{ marginTop: '10px', marginBottom: '10px', marginRight: '10px' }}>
        {genTagForPosition(position)} {attendeePositions[position]}
      </span>
    );
  }

  return (
    <Elements stripe={promise}>
      <Card
        onClick={() => {
          if (studentRegistrationEnabled) {
            setShownModal('checkout');
          } else {
            alert("Sorry, registration for that class is now closed.");
          }
        }}
        hoverable={studentRegistrationEnabled}
        type="inner"
        className={`ClassCard card-custom ${showButtons ? 'show-bottom-btns' : ''} ${studentRegistrationEnabled ? '' : 'registration-disabled'}`}
        id={uuid}
        cover={(
          <Image
            width={320}
            height={200}
            preview={false}
            src={img || 'error'}
            fallback={errorImg}
          />
        )}
        actions={showButtons &&
          [<ProfileOutlined
            key="roster"
            onClick={(e) => {
              setShownModal('roster');
              e.stopPropagation();
            }}
          />].concat(
          user[0].role === "admin" ?
            [
              <EditOutlined
                key="edit"
                onClick={(e) => {
                  setShownModal('edit');
                  e.stopPropagation();
                }}
              />,
              <Popconfirm
                title="Are you sure you want to delete this class?"
                onConfirm={(e) => { e.stopPropagation(); (async () => { // stopPropagation needs to be synchronous
                  const currentUserSession = await Auth.currentSession();
                  await window.fetch(`${config.serverUrl}classes/delete/${uuid}/${user[0].email}`, {
                    method: 'delete',
                    headers: {
                      'Content-Type': 'application/json',
                      'accesstoken': currentUserSession.getAccessToken().getJwtToken(),
                    }
                  });
                  await getClasses();
                })(); }}
                onCancel={(e) => e.stopPropagation()}
              >
                <DeleteOutlined onClick={(e) => e.stopPropagation()}/>
              </Popconfirm>
            ] : []
          )
        }
      >
        <Card.Meta
          title={title}

        />
        <ClassDescriptionContainer content={
          <Fragment>
            <hr />
            <span>{formatTime(startTime)} - {formatTime(endTime)}, {daysOfWeek.join(', ')}</span>
            <br />
            <b>Dates:</b> <span>{startDate} to {endDate}</span>
            <br />
            <b>Instructors:</b>
            {' '}
            {instructors}
            <br />
            <b>Price:</b>
            {' $'}
            {price}
            <p>{content}</p>
          </Fragment>
        } />
      </Card>
      <Modal
        visible={shownModal === 'roster'}
        onCancel={() => setShownModal(null)}
        footer={null}
        title={`${title} Roster`}
        width="75%"
      >
        <Button
          type="primary"
          disable={reChecking}
          style={{
            width: '175px',
            display: 'block',
            float: 'right'
          }}
          onClick={async (e) => {
            e.preventDefault();
            setReChecking(true);

            try {
              const currentUserSession = await Auth.currentSession();
              const rebalanceResponse = await window.fetch(
                `${config.serverUrl}classes/rebalance/${uuid}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'accesstoken': currentUserSession.getAccessToken().getJwtToken(),
                  },
                });
              const { attendees } = await rebalanceResponse.json();

              setClassAttendees(attendees);
              setReChecking(false);
            } catch (e) {
              console.error(e);
            }
          }}
        >
          {
            reChecking
              ? <Spin indicator={<LoadingOutlined style={{ fontSize: 12, color: 'white' }} spin />} className="spinner-placeholder" />
              : <span>Re-Check Waitlisted</span>
          }
        </Button>
        {
          Object.keys(attendeePositions).length > 0 && (
            <div style={{ paddingBottom: '20px' }}>
              {positionCount('lead')}
              {positionCount('follow')}
              {positionCount('either')}
              <span style={{ marginLeft: '20px' }}><b>Total:</b> {classAttendees.length}</span>
            </div>
          )
        }

        {
          classAttendees && classAttendees.length ? (
            <CenTable title="Roster" columns={columnsSchema} dataSource={classAttendees} onSave={onSave} />
          ) : <Empty description="No students" />
        }

        <div className="add-student-container">
          <Button
            onClick={() => setDisplayAddStudentForm(displayAddStudentForm ? 0 : 'auto')}
            type="primary">+ Add New Student</Button>
          <AnimateHeight
            duration={dropdownAnimationTime}
            height={displayAddStudentForm}
          >
            <AddAStudentForm
              classUuid={uuid}
              setClassAttendees={setClassAttendees}
              setDisplayAddStudentForm={setDisplayAddStudentForm}
            />
          </AnimateHeight>
        </div>

      </Modal>
      <Modal
        visible={shownModal === 'edit'}
        onCancel={() => setShownModal(null)}
        footer={null}
        title={`Edit ${title}`}
      >
        <ClassForm
          allCardsData={allCardsData}
          setClassesUI={setClassesUI}
          allDisplayedCardsData={allDisplayedCardsData}
          defaultForm={card}
          isEditingClass={shownModal === 'edit'}
          getClasses={getClasses}
          closeClassForm={() => setShownModal(null)} />
      </Modal>
      <Modal
        visible={shownModal === 'checkout'}
        onCancel={() => setShownModal(null)}
        footer={null}
        title={`Register for ${title}`}
      >
        <CheckoutFlow
          title={title}
          price={price}
          uuid={uuid}
          handleCheckoutModalClose={() => setShownModal(null)}
          leadFollowClass={leadFollowClass}
        />
      </Modal>
    </Elements>
  );
}
